import DrawerContent from 'components/common/Drawer/DrawerContent';
import { createContext } from 'components/common/Drawer/DrawerContext';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const DrawerProvider = ({
  children,
  state: stateProp,
  visible: visibleProp,
  title: titleProp,
  actions,
  drawerContent,
  name,
  onClose,
  onSubmit,
  onShow,
  sx = {},
}) => {
  const drawerContentRef = useRef();
  const [titleLocal, setTitleLocal] = useState('');
  const [stateLocal, setStateLocal] = useState();
  const [visibleLocal, setVisibleLocal] = useState();

  const show = useCallback(
    newState => {
      if (stateProp === undefined) {
        setStateLocal(newState);
      }
      if (visibleProp === undefined) {
        setVisibleLocal(true);
      }
      if (onShow) {
        onShow(newState);
      }
    },
    [onShow, stateProp, visibleProp],
  );

  const hide = useCallback(() => {
    if (visibleProp === undefined) {
      setVisibleLocal(false);
    }
    if (onClose) {
      onClose();
    }
  }, [onClose, visibleProp]);

  useEffect(() => {
    if (drawerContentRef.current) {
      drawerContentRef.current.setOnSubmit(onSubmit);
    }
  }, [onSubmit]);

  const setOnSubmit = useCallback(newOnSubmit => {
    if (drawerContentRef.current) {
      drawerContentRef.current.setOnSubmit(newOnSubmit);
    }
  }, []);

  const visible = useMemo(
    () => (visibleProp === undefined ? visibleLocal : visibleProp),
    [visibleLocal, visibleProp],
  );
  const state = useMemo(
    () => (stateProp === undefined ? stateLocal : stateProp),
    [stateLocal, stateProp],
  );
  const title = useMemo(
    () => (titleProp === undefined ? titleLocal : titleProp),
    [titleLocal, titleProp],
  );
  const [Context, setContext] = useState();
  useEffect(() => {
    setContext(createContext(name));
  }, [name]);
  const contextValue = useMemo(
    () => ({ show, hide, visible, title, setTitle: setTitleLocal, state, setOnSubmit }),
    [show, hide, visible, title, setTitleLocal, state, setOnSubmit],
  );
  if (!Context) {
    return null;
  }
  return (
    <Context.Provider value={contextValue}>
      {typeof children === 'function' ? children({ show, hide }) : children}
      {visible && (
        <DrawerContent
          ref={drawerContentRef}
          drawer={name}
          title={title}
          actions={actions}
          onSubmit={onSubmit}
          onClose={onClose}
          sx={sx}
        >
          {drawerContent}
        </DrawerContent>
      )}
    </Context.Provider>
  );
};

DrawerProvider.propTypes = {
  children: PropTypes.node,
  state: PropTypes.object,
  visible: PropTypes.bool,
  drawerContent: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  actions: PropTypes.node,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  onShow: PropTypes.func,
  sx: PropTypes.object,
};

export default DrawerProvider;
